import { type ToolSpec, n, s, fmt, money } from "./types";

export const MAKER_TOOLS: ToolSpec[] = [
  {
    slug: "etsy-shipping-margin-calculator",
    cluster: "finance",
    name: "Etsy Shipping Margin",
    h1: "Etsy & E-Commerce Shipping Margin & Packing Box Cost Calculator",
    title: "Etsy Shipping Margin & Box Cost Calculator | Free (2026)",
    description:
      "Calculate net shipping profit and true order margin instantly. Free online Etsy shipping margin calculator with live formulas, unit conversion and a step-by-step breakdown.",
    keywords: [
      "etsy shipping margin calculator",
      "does etsy shipping cost eat my profit",
      "packing box and materials cost calculator for sellers",
      "how much should i charge for shipping on etsy",
      "e-commerce net margin after shipping calculator",
      "free shipping vs charged shipping profit calculator",
    ],
    inputs: [
      { id: "price", label: "Item price", type: "number", def: "35", unit: "$", min: 0, step: 0.01 },
      { id: "cogs", label: "Cost of goods (COGS)", type: "number", def: "10", unit: "$", min: 0, step: 0.01 },
      { id: "box", label: "Box / packaging cost", type: "number", def: "1.20", unit: "$", min: 0, step: 0.01 },
      { id: "materials", label: "Materials cost (tape, tissue, inserts)", type: "number", def: "0.80", unit: "$", min: 0, step: 0.01 },
      { id: "label", label: "Shipping label cost", type: "number", def: "6.50", unit: "$", min: 0, step: 0.01 },
      { id: "charged", label: "Shipping charged to buyer", type: "number", def: "5.99", unit: "$", min: 0, step: 0.01 },
    ],
    compute: (v) => {
      const price = n(v, "price"), cogs = n(v, "cogs"), box = n(v, "box"), materials = n(v, "materials");
      const label = n(v, "label"), charged = n(v, "charged");
      const fulfilment = box + materials + label;
      const netShip = charged - fulfilment;
      const revenue = price + charged;
      const grossProfit = revenue - (cogs + fulfilment);
      const margin = revenue > 0 ? (grossProfit / revenue) * 100 : 0;
      return {
        cards: [
          { label: "Fulfilment cost", value: money(fulfilment), sub: "Box + materials + label", accent: false },
          { label: "Net shipping P/L", value: money(netShip), sub: netShip >= 0 ? "Shipping covers itself" : "You lose on shipping", accent: true },
          { label: "Total order gross profit", value: money(grossProfit), sub: "Revenue minus COGS and fulfilment" },
          { label: "Net margin", value: `${fmt(margin, 1)}%`, sub: "Of total order revenue" },
        ],
        table: {
          title: "Order breakdown",
          head: ["Line item", "Amount"],
          rows: [
            ["Item price", money(price)],
            ["Shipping charged", money(charged)],
            ["Total revenue", money(revenue)],
            ["COGS", money(cogs)],
            ["Fulfilment cost", money(fulfilment)],
            ["Net shipping profit/loss", money(netShip)],
            ["Gross profit", money(grossProfit)],
            ["Net margin %", `${fmt(margin, 1)}%`],
          ],
        },
        summary: netShip >= 0
          ? `You collect ${money(netShip)} more in shipping than it costs to pack and ship this order.`
          : `You absorb ${money(Math.abs(netShip))} of shipping cost on every one of these orders.`,
      };
    },
    intro:
      "Most Etsy and small e-commerce sellers price the product carefully but treat shipping as an afterthought, only to discover at tax time that packaging and postage quietly ate the margin. This calculator separates the shipping line from the product line so you can see exactly which one is losing money.",
    sections: [
      { h: "Why shipping profit and order profit are different numbers", body: "A seller can charge $5.99 for shipping, pay $6.50 for the label plus $2 in box and materials, and lose $2.51 on shipping alone — even while the overall order is still profitable because the item price covers the gap. Tracking both numbers separately tells you whether to raise the item price or the shipping charge." },
      { h: "Free shipping is not free", body: "'Free shipping' offers simply move the fulfilment cost into the item price. If you switch to free shipping, add your current fulfilment cost to the item price to keep margin constant, then round to a psychologically clean number." },
      { h: "Box and materials add up faster than sellers expect", body: "Bubble mailers, branded tissue paper, thank-you cards, and tape can total $1–$3 per order that never shows up on a supplier invoice. Weigh and cost your exact packaging once and reuse that number for every calculation." },
      { h: "Using this for multi-item orders", body: "For bundles, sum all item COGS into the COGS field and all box/insert costs used for that single shipment into materials, then run one calculation per shipment rather than per item to see the true parcel-level economics." },
    ],
    staticTable: {
      title: "Typical US small-parcel shipping costs (reference)",
      head: ["Carrier service", "Typical cost", "Best for"],
      rows: [
        ["USPS Ground Advantage", "$5.50–$9", "Under 1 lb, non-urgent"],
        ["USPS Priority Mail", "$8–$14", "1–5 lb, 2-3 day"],
        ["UPS Ground", "$9–$16", "Heavier, durable goods"],
        ["Poly mailer", "$0.10–$0.30", "Soft goods"],
        ["Small box + void fill", "$0.80–$2.50", "Fragile/rigid items"],
        ["Branded insert card", "$0.05–$0.25", "Unboxing experience"],
      ],
    },
    faqs: [
      { q: "Should I charge exact shipping cost or a flat rate?", a: "A flat rate that slightly overcharges light orders and slightly undercharges heavy ones usually nets out even across your catalogue, and it is far less confusing to buyers than exact per-order rates." },
      { q: "What counts as fulfilment cost?", a: "Box or mailer, void fill, tape, labels, inserts, and the postage label itself. It excludes your time, which should instead be reflected in the item price or a handling fee." },
      { q: "Is negative net shipping profit always bad?", a: "Not necessarily — some sellers intentionally subsidise shipping slightly to keep the displayed shipping charge attractive, as long as the overall order margin still meets their target." },
      { q: "How do I account for Etsy's payment processing fees?", a: "Add roughly 6.5% of the total order revenue plus a small flat fee as an extra cost line before calculating margin if you want an all-in profit figure; this tool focuses purely on shipping and packaging." },
      { q: "Does this work for international shipping?", a: "Yes — just enter the actual label cost in your currency along with any customs or duty handling fees added to the label field." },
      { q: "What is a healthy net margin for handmade goods?", a: "Many profitable Etsy sellers target 30-50% net margin after all fulfilment and platform costs, though highly material-intensive categories may run lower." },
      { q: "Why is my gross profit positive but shipping is negative?", a: "Because the item price is subsidising the shipping shortfall. It is still fine financially, but raising the shipping charge slightly would let you lower the item price for better search visibility." },
      { q: "Should returns and lost packages be factored in?", a: "For a full profitability picture, build an expected loss rate (e.g. 2% of orders) into your COGS as an amortised cost rather than into this per-order calculation." },
      { q: "How often should I recheck this calculation?", a: "Recheck whenever carrier rates change (usually annually) or whenever you switch box or material suppliers, since even small per-unit changes compound across volume." },
      { q: "Can I use this for wholesale or B2B shipments?", a: "Yes, the same fulfilment and margin logic applies — just substitute your wholesale price and any freight-specific packaging costs." },
    ],
    formula: {
      expressions: [
        "Fulfilment = Box + Materials + Label",
        "NetShippingProfit = Charged − Fulfilment",
        "Revenue = ItemPrice + Charged",
        "Margin% = ((Revenue − (COGS + Fulfilment)) / Revenue) × 100",
      ],
      variables: [
        { name: "Box", meaning: "Packaging/box cost" },
        { name: "Materials", meaning: "Tape, tissue, inserts" },
        { name: "Label", meaning: "Shipping label cost" },
        { name: "Charged", meaning: "Shipping fee charged to buyer" },
        { name: "COGS", meaning: "Cost of goods sold" },
      ],
    },
    related: ["substack-patreon-fee-calculator", "tiktok-live-gift-calculator", "3d-print-cost-calculator", "epoxy-resin-calculator", "candle-wax-fragrance-calculator"],
  },

  {
    slug: "epoxy-resin-calculator",
    cluster: "lifestyle",
    name: "Epoxy Resin Volume & Mix Ratio",
    h1: "Epoxy Resin Volume & Part A:B Mixing Ratio Calculator (Oz, ml, Grams)",
    title: "Epoxy Resin Volume & Mix Ratio Calculator | Free (2026)",
    description:
      "Calculate exact resin and hardener quantities instantly. Free online epoxy resin calculator with live formulas, unit conversion and a step-by-step breakdown.",
    keywords: [
      "epoxy resin calculator ounces to fill mold",
      "how much resin do i need for a mold",
      "resin part a part b mixing ratio calculator",
      "epoxy resin volume calculator ml grams",
      "resin calculator for river table or coaster",
      "how much resin for cylinder mold",
    ],
    inputs: [
      { id: "shape", label: "Mold shape", type: "select", def: "rectangle", options: [
        { value: "rectangle", label: "Rectangle / slab" },
        { value: "cylinder", label: "Cylinder" },
        { value: "round", label: "Round slab (disc)" },
      ] },
      { id: "length", label: "Length (inches, or diameter for round/cylinder)", type: "number", def: "8", unit: "in", min: 0.1, step: 0.1 },
      { id: "width", label: "Width (inches, ignored for cylinder/round)", type: "number", def: "6", unit: "in", min: 0.1, step: 0.1 },
      { id: "depth", label: "Depth / thickness", type: "number", def: "0.25", unit: "in", min: 0.01, step: 0.01 },
      { id: "ratio", label: "Mix ratio (A:B)", type: "select", def: "1:1", options: [
        { value: "1:1", label: "1:1" },
        { value: "2:1", label: "2:1" },
        { value: "3:1", label: "3:1" },
      ] },
      { id: "waste", label: "Waste buffer", type: "number", def: "10", unit: "%", min: 0, max: 50, step: 1 },
    ],
    compute: (v) => {
      const shape = s(v, "shape"), length = n(v, "length"), width = n(v, "width"), depth = n(v, "depth");
      const ratio = s(v, "ratio"), waste = n(v, "waste");
      let cubicIn = 0;
      if (shape === "rectangle") cubicIn = length * width * depth;
      else if (shape === "cylinder") cubicIn = Math.PI * Math.pow(length / 2, 2) * depth;
      else cubicIn = Math.PI * Math.pow(length / 2, 2) * depth;
      const baseOz = cubicIn * 0.554;
      const totalOz = baseOz * (1 + waste / 100);
      const totalMl = totalOz * 29.5735;
      const totalGrams = totalMl * 1.1;
      const [pa, pb] = ratio.split(":").map(Number);
      const totalParts = pa + pb || 1;
      const partAOz = totalOz * (pa / totalParts);
      const partBOz = totalOz * (pb / totalParts);
      return {
        cards: [
          { label: "Total resin needed", value: `${fmt(totalOz, 2)} fl oz`, sub: `${fmt(totalMl, 0)} ml`, accent: true },
          { label: "Weight", value: `${fmt(totalGrams, 0)} g`, sub: "At ~1.1 g/ml density" },
          { label: `Part A (${ratio})`, value: `${fmt(partAOz, 2)} oz`, sub: `${fmt(partAOz * 29.5735, 0)} ml` },
          { label: `Part B (${ratio})`, value: `${fmt(partBOz, 2)} oz`, sub: `${fmt(partBOz * 29.5735, 0)} ml` },
        ],
        table: {
          title: "Pour breakdown",
          head: ["Measure", "Value"],
          rows: [
            ["Mold volume", `${fmt(cubicIn, 2)} cu in`],
            ["Base resin (no buffer)", `${fmt(baseOz, 2)} fl oz`],
            ["Waste buffer applied", `${waste}%`],
            ["Total fl oz", fmt(totalOz, 2)],
            ["Total ml", fmt(totalMl, 0)],
            ["Total grams", fmt(totalGrams, 0)],
            ["Part A", `${fmt(partAOz, 2)} oz / ${fmt(partAOz * 29.5735 * 1.1, 0)} g`],
            ["Part B", `${fmt(partBOz, 2)} oz / ${fmt(partBOz * 29.5735 * 1.1, 0)} g`],
          ],
        },
        summary: `Mix ${fmt(partAOz, 2)} oz of Part A with ${fmt(partBOz, 2)} oz of Part B for a ${ratio} ratio pour, including a ${waste}% waste buffer.`,
      };
    },
    intro:
      "Running short of resin mid-pour ruins a project, and mixing an inaccurate Part A:B ratio can leave a cast that never fully cures. This calculator converts your mold dimensions into exact fluid ounces, millilitres and grams, then splits the total by your resin's mix ratio.",
    sections: [
      { h: "Why the waste buffer matters", body: "Resin left in mixing cups, on stir sticks, and on the sides of measuring containers typically wastes 5-15% of what you pour. A 10% buffer is a safe default for small pieces; increase it to 15-20% for intricate molds with lots of surface area relative to volume." },
      { h: "Rectangle, cylinder and round slab formulas", body: "Rectangular and slab molds use length × width × depth in cubic inches. Round shapes — cylinders, coasters, discs — use the circle area formula π × radius² × depth, where radius is half the diameter you enter in the length field." },
      { h: "Converting cubic inches to fluid ounces and grams", body: "One cubic inch holds approximately 0.554 fluid ounces of liquid. Because most casting resins sit close to 1.1 grams per millilitre once mixed, converting ounces to millilitres (×29.5735) and then to grams (×1.1) gives a close-enough weight for a kitchen or jewellery scale." },
      { h: "Reading your specific mix ratio", body: "Manufacturers label ratios by volume or by weight — always check which one your product uses. If your resin specifies a weight-based ratio, weigh both parts on a scale rather than relying purely on the ounce figures here." },
    ],
    staticTable: {
      title: "Common resin quantities by project",
      head: ["Project", "Typical volume", "Approx. total resin"],
      rows: [
        ["Jewellery pendant mold", "1-2 cu in", "0.5-1 fl oz"],
        ["Coaster (4in round, 0.25in)", "3.1 cu in", "1.7 fl oz"],
        ["8x10in tray, 0.25in deep", "20 cu in", "11 fl oz"],
        ["River table (36x12in, 1in gap avg)", "~90 cu in", "50 fl oz"],
        ["Bar top (72x24in, 0.125in)", "216 cu in", "120 fl oz"],
        ["Tumbler coating (16oz cup)", "N/A", "1-2 fl oz"],
      ],
    },
    faqs: [
      { q: "How much resin do I need for a mold I don't know the exact shape of?", a: "Fill the mold with water first, then measure the water volume in fluid ounces — that is your true target volume, more accurate than any geometric formula for irregular shapes." },
      { q: "What happens if I get the mix ratio wrong?", a: "Too much hardener causes rapid, hot curing with bubbling and cracking; too little leaves a sticky, permanently soft surface. Always measure by the manufacturer's exact ratio, not by eye." },
      { q: "Should I measure resin by volume or weight?", a: "Weight is more accurate because viscosity differences make volume measurements imprecise, especially with thick tabletop resins — use a gram scale whenever the product's instructions specify weight ratios." },
      { q: "Why is my resin calculator result different from the bottle's coverage chart?", a: "Coverage charts assume a specific thickness, usually 1/8 inch per coat; this calculator uses your exact depth input, which is more accurate for anything other than that standard thickness." },
      { q: "Can I pour resin thicker than the depth I calculated?", a: "Most casting resins have a maximum safe pour depth (often 0.5-2 inches) before excess heat causes cracking or yellowing — check your product's exothermic limit before scaling up depth." },
      { q: "How much extra should I mix for a river table?", a: "Because deep pours often need multiple layers, add a 15-20% buffer per layer and always mix slightly more than calculated rather than running short mid-pour." },
      { q: "Does temperature affect how much resin I need?", a: "No, temperature affects cure time and viscosity, not volume, though warmer resin flows more easily and self-levels better into corners." },
      { q: "Is 1.1 g/ml density accurate for all resins?", a: "It's a close average; most epoxy casting and tabletop resins fall between 1.05 and 1.15 g/ml combined, so this is accurate within a few percent for weight estimates." },
      { q: "Can I use this calculator for silicone mold making instead of resin?", a: "The volume math is identical, but silicone mix ratios and densities differ, so check your specific silicone product's ratio and density before using the weight outputs." },
      { q: "Why does my resin look cloudy or have bubbles after following this exactly?", a: "Volume accuracy doesn't fix mixing technique — under-mixing causes cloudiness and bubbles, so always mix for the full time recommended (typically 3-5 minutes) and consider a heat gun pass to release trapped air." },
    ],
    formula: {
      expressions: [
        "RectangleVolume(cu in) = Length × Width × Depth",
        "RoundVolume(cu in) = π × (Diameter/2)² × Depth",
        "TotalOz = Volume(cu in) × 0.554 × (1 + Waste%/100)",
        "Grams = Oz × 29.5735 × 1.1",
        "PartA = Total × (RatioA / (RatioA+RatioB))",
      ],
      variables: [
        { name: "Depth", meaning: "Pour thickness in inches" },
        { name: "Waste%", meaning: "Buffer for spillage/residue" },
        { name: "RatioA:RatioB", meaning: "Resin to hardener mix ratio" },
      ],
    },
    related: ["candle-wax-fragrance-calculator", "3d-print-cost-calculator", "concrete-bag-calculator", "etsy-shipping-margin-calculator"],
  },

  {
    slug: "candle-wax-fragrance-calculator",
    cluster: "lifestyle",
    name: "Candle Wax & Fragrance",
    h1: "Candle Making Wax & Fragrance Oil Calculator: Exact Grams & Ounces",
    title: "Candle Wax & Fragrance Oil Calculator | Free (2026)",
    description:
      "Calculate exact wax and fragrance oil weight instantly. Free online candle making calculator with live formulas, unit conversion and a step-by-step breakdown.",
    keywords: [
      "candle wax calculator grams and ounces",
      "how much fragrance oil per pound of wax",
      "soy wax calculator for candle containers",
      "candle making fragrance load percentage calculator",
      "wax needed for multiple candle containers",
      "coconut wax vs soy wax quantity calculator",
    ],
    inputs: [
      { id: "containers", label: "Number of containers", type: "number", def: "4", min: 1, max: 200, step: 1 },
      { id: "capacity", label: "Container water capacity (per container)", type: "number", def: "8", unit: "oz", min: 0.1, step: 0.1 },
      { id: "fragrance", label: "Fragrance load", type: "number", def: "8", unit: "%", min: 6, max: 12, step: 0.5 },
      { id: "wax", label: "Wax type", type: "select", def: "soy", options: [
        { value: "soy", label: "Soy wax (0.86 fill factor)" },
        { value: "paraffin", label: "Paraffin wax (0.88 fill factor)" },
        { value: "coconut", label: "Coconut wax (0.85 fill factor)" },
      ] },
    ],
    compute: (v) => {
      const containers = n(v, "containers"), capacity = n(v, "capacity"), fr = n(v, "fragrance"), waxType = s(v, "wax");
      const factors: Record<string, number> = { soy: 0.86, paraffin: 0.88, coconut: 0.85 };
      const factor = factors[waxType] ?? 0.86;
      const perContainerCapWeightOz = capacity * factor;
      const perContainerWaxOz = fr >= 0 ? perContainerCapWeightOz / (1 + fr / 100) : perContainerCapWeightOz;
      const perContainerFragOz = perContainerWaxOz * (fr / 100);
      const totalWaxOz = perContainerWaxOz * containers;
      const totalFragOz = perContainerFragOz * containers;
      const ozToG = 28.3495;
      return {
        cards: [
          { label: "Total wax needed", value: `${fmt(totalWaxOz, 2)} oz`, sub: `${fmt(totalWaxOz * ozToG, 0)} g`, accent: true },
          { label: "Total fragrance oil", value: `${fmt(totalFragOz, 2)} oz`, sub: `${fmt(totalFragOz * ozToG, 0)} g` },
          { label: "Per container wax", value: `${fmt(perContainerWaxOz, 2)} oz`, sub: `${fmt(perContainerWaxOz * ozToG, 0)} g` },
          { label: "Per container fragrance", value: `${fmt(perContainerFragOz, 2)} oz`, sub: `${fmt(perContainerFragOz * ozToG, 0)} g` },
        ],
        table: {
          title: "Batch breakdown",
          head: ["Measure", "Total", "Per container"],
          rows: [
            ["Wax (oz)", fmt(totalWaxOz, 2), fmt(perContainerWaxOz, 2)],
            ["Wax (g)", fmt(totalWaxOz * ozToG, 0), fmt(perContainerWaxOz * ozToG, 0)],
            ["Fragrance oil (oz)", fmt(totalFragOz, 2), fmt(perContainerFragOz, 2)],
            ["Fragrance oil (g)", fmt(totalFragOz * ozToG, 0), fmt(perContainerFragOz * ozToG, 0)],
            ["Fragrance load", `${fr}%`, `${fr}%`],
            ["Containers", fmt(containers), "1"],
          ],
        },
        summary: `For ${containers} container(s), melt ${fmt(totalWaxOz, 2)} oz of ${waxType} wax and blend in ${fmt(totalFragOz, 2)} oz of fragrance oil at a ${fr}% load.`,
      };
    },
    intro:
      "Guessing wax and fragrance quantities leads to sunken tops, weak scent throw or overpowering candles that never fully cure. This calculator uses each wax type's fill factor and your chosen fragrance load to give exact gram and ounce measurements for any batch size.",
    sections: [
      { h: "What a fill factor actually accounts for", body: "Water capacity and wax weight are not the same because wax is less dense than water. A container's fill factor (0.85-0.88 depending on wax) converts fluid ounce capacity into the actual weight of wax needed to fill it to the same level, before fragrance is blended in." },
      { h: "Choosing a fragrance load", body: "Most soy and coconut wax candles perform well between 6% and 10% fragrance load; paraffin can often support up to 12% without sweating. Going above the wax manufacturer's recommended maximum risks fragrance separating and pooling on the surface after curing." },
      { h: "Why fragrance is subtracted from, not added to, the wax weight", body: "This calculator treats fragrance oil as part of the total fill weight, matching how most candle makers formulate: the wax portion is reduced slightly so wax plus fragrance still equals the target fill weight, keeping wax levels consistent in the container." },
      { h: "Scaling a recipe up for larger batches", body: "Multiply the per-container figures by your container count rather than recalculating capacity, since fill factor and fragrance load stay constant regardless of batch size — this calculator does that scaling for you automatically." },
    ],
    staticTable: {
      title: "Fragrance load quick reference by wax type",
      head: ["Wax type", "Typical range", "Fill factor", "Notes"],
      rows: [
        ["Soy wax", "6-10%", "0.86", "Prone to frosting above 10%"],
        ["Paraffin wax", "6-12%", "0.88", "Handles higher loads well"],
        ["Coconut wax", "6-9%", "0.85", "Often blended with soy"],
        ["Coconut-soy blend", "7-10%", "0.855", "Popular for luxury candles"],
        ["Beeswax", "3-5%", "0.95", "Naturally scented, low FO"],
        ["Palm wax", "6-8%", "0.87", "Crystalline finish"],
      ],
    },
    faqs: [
      { q: "What is a fragrance load percentage?", a: "It's the weight of fragrance oil as a percentage of the total wax-plus-fragrance weight. An 8% load in a 10 oz total pour means 0.8 oz is fragrance oil and 9.2 oz is wax." },
      { q: "Can I use more fragrance oil than the wax manufacturer recommends?", a: "You can, but exceeding the maximum (usually printed on the wax bag, often 10-12%) risks fragrance oil sweating out onto the candle's surface or destabilising the wax structure." },
      { q: "Why did my candle sink in the middle after cooling?", a: "Sinking is usually caused by cooling too quickly or uneven container temperature, not wax quantity — this calculator gets you the right total volume, but pour and cure technique still matter." },
      { q: "Do I need to adjust for wick material?", a: "No, wick size is chosen based on container diameter and wax type, independent of the wax and fragrance weight calculated here." },
      { q: "Is water capacity the same as the volume printed on the jar?", a: "Not always — printed volumes sometimes refer to nominal size rather than true capacity, so weighing water to the fill line you intend to use gives the most accurate number." },
      { q: "How do I convert this recipe to grams if my scale only does grams?", a: "Every value on this page already includes a gram conversion alongside ounces, using the standard 28.35 grams per ounce conversion." },
      { q: "Can I mix two wax types together?", a: "Yes, blended waxes (like coconut-soy) are common — use the fill factor of your dominant wax or an average of the two as a close approximation." },
      { q: "Why is my calculated wax amount different from what actually fit the jar?", a: "Small variances come from pour temperature and whether the wax was measured before or after melting; always weigh solid wax before melting for the most consistent results." },
      { q: "Does higher fragrance load mean stronger cold throw?", a: "Generally yes up to the wax's saturation point, but scent strength also depends heavily on the fragrance oil's own strength and how long the candle cures before burning." },
      { q: "How long should a candle cure before burning?", a: "Most soy and coconut wax candles benefit from a 1-2 week cure time at room temperature to let the fragrance oil fully bind with the wax for maximum scent throw." },
    ],
    formula: {
      expressions: [
        "CapacityWeight = WaterCapacity(oz) × FillFactor",
        "WaxWeight = CapacityWeight / (1 + FragranceLoad%/100)",
        "FragranceWeight = WaxWeight × FragranceLoad%/100",
      ],
      variables: [
        { name: "FillFactor", meaning: "Wax density factor relative to water (0.85-0.88)" },
        { name: "FragranceLoad%", meaning: "Percent fragrance oil of total pour weight" },
      ],
    },
    related: ["epoxy-resin-calculator", "3d-print-cost-calculator", "bbq-charcoal-wood-calculator", "etsy-shipping-margin-calculator"],
  },

  {
    slug: "3d-print-cost-calculator",
    cluster: "digital",
    name: "3D Print Cost",
    h1: "3D Printing Filament Length, Weight & Total Print Cost Calculator",
    title: "3D Print Filament Weight & Cost Calculator | Free (2026)",
    description:
      "Calculate print weight and total filament cost instantly. Free online 3D printing cost calculator with live formulas, unit conversion and a step-by-step breakdown.",
    keywords: [
      "3d print cost calculator filament weight",
      "how much does a 3d print cost in electricity",
      "filament length to grams calculator",
      "3d printing spool cost per print calculator",
      "PLA ABS PETG weight calculator",
      "cost per hour to run a 3d printer",
    ],
    inputs: [
      { id: "material", label: "Filament type", type: "select", def: "PLA", options: [
        { value: "PLA", label: "PLA (1.24 g/cm³)" },
        { value: "ABS", label: "ABS (1.04 g/cm³)" },
        { value: "PETG", label: "PETG (1.27 g/cm³)" },
      ] },
      { id: "diameter", label: "Filament diameter", type: "select", def: "1.75", options: [
        { value: "1.75", label: "1.75 mm" },
        { value: "2.85", label: "2.85 mm" },
      ] },
      { id: "length", label: "Filament length used", type: "number", def: "25", unit: "m", min: 0.1, step: 0.1 },
      { id: "spoolCost", label: "Spool cost", type: "number", def: "22", unit: "$/kg", min: 0, step: 0.1 },
      { id: "watts", label: "Printer power draw", type: "number", def: "120", unit: "W", min: 1, step: 1 },
      { id: "hours", label: "Print time", type: "number", def: "4", unit: "hours", min: 0.1, step: 0.1 },
      { id: "rate", label: "Electricity rate", type: "number", def: "0.16", unit: "$/kWh", min: 0, step: 0.01 },
    ],
    compute: (v) => {
      const densities: Record<string, number> = { PLA: 1.24, ABS: 1.04, PETG: 1.27 };
      const material = s(v, "material"), diameter = n(v, "diameter"), length = n(v, "length");
      const spoolCost = n(v, "spoolCost"), watts = n(v, "watts"), hours = n(v, "hours"), rate = n(v, "rate");
      const density = densities[material] ?? 1.24;
      const lengthCm = length * 100;
      const radiusCm = diameter / 20; // diameter mm / 2 / 10
      const volumeCm3 = Math.PI * Math.pow(radiusCm, 2) * lengthCm;
      const weightG = volumeCm3 * density;
      const materialCost = (weightG / 1000) * spoolCost;
      const powerKwh = (watts * hours) / 1000;
      const powerCost = powerKwh * rate;
      const totalCost = materialCost + powerCost;
      const costPerHour = hours > 0 ? totalCost / hours : 0;
      return {
        cards: [
          { label: "Print weight", value: `${fmt(weightG, 1)} g`, sub: `${material} at ${density} g/cm³`, accent: false },
          { label: "Material cost", value: money(materialCost, "$", 2), sub: `${fmt(weightG, 1)}g of spool used` },
          { label: "Total print cost", value: money(totalCost, "$", 2), sub: `Material + ${money(powerCost, "$", 2)} power`, accent: true },
          { label: "Cost per hour", value: money(costPerHour, "$", 2), sub: `Over ${fmt(hours, 1)}h print time` },
        ],
        table: {
          title: "Cost breakdown",
          head: ["Item", "Value"],
          rows: [
            ["Filament diameter", `${diameter} mm`],
            ["Filament length", `${fmt(length, 2)} m`],
            ["Volume used", `${fmt(volumeCm3, 2)} cm³`],
            ["Weight", `${fmt(weightG, 1)} g`],
            ["Material cost", money(materialCost, "$", 2)],
            ["Power used", `${fmt(powerKwh, 3)} kWh`],
            ["Power cost", money(powerCost, "$", 2)],
            ["Total cost", money(totalCost, "$", 2)],
          ],
        },
        summary: `This print uses ${fmt(weightG, 1)}g of ${material} and costs ${money(totalCost, "$", 2)} total, or ${money(costPerHour, "$", 2)} per hour.`,
      };
    },
    intro:
      "Slicer software tells you filament length, but rarely the real dollar cost once electricity is added in. This calculator converts filament length and diameter into weight using material density, then adds material and electricity cost together for a true per-print and per-hour figure.",
    sections: [
      { h: "Why filament diameter changes weight so much", body: "A 2.85mm filament has roughly 2.65 times the cross-sectional area of 1.75mm filament for the same length, so the same slicer-reported length in metres produces far more weight and cost on thicker filament — always check which diameter your printer uses." },
      { h: "Material density differences matter", body: "PETG (1.27 g/cm³) is denser than PLA (1.24) and noticeably denser than ABS (1.04), meaning the same physical print volume weighs differently and costs differently depending on material choice, even at identical spool prices." },
      { h: "Electricity cost is usually small but not zero", body: "A typical desktop FDM printer draws 80-150W, and at $0.16/kWh a 6-hour print only costs $0.08-$0.14 in electricity — small per print, but worth including for shops calculating true per-unit costs across hundreds of prints." },
      { h: "Using this for pricing prints commercially", body: "Add a markup multiplier (commonly 3-5x material plus labour) on top of the total cost this tool produces if you are pricing prints for sale, since raw material and power cost alone never covers printer depreciation, failed prints or your time." },
    ],
    staticTable: {
      title: "Filament density and typical spool pricing",
      head: ["Material", "Density (g/cm³)", "Typical spool price/kg", "Common use"],
      rows: [
        ["PLA", "1.24", "$18-$25", "General purpose, prototypes"],
        ["ABS", "1.04", "$18-$28", "Functional, heat-resistant parts"],
        ["PETG", "1.27", "$20-$30", "Outdoor, durable parts"],
        ["TPU (flexible)", "1.21", "$25-$40", "Flexible parts, phone cases"],
        ["Nylon", "1.14", "$35-$60", "High-strength mechanical parts"],
        ["ASA", "1.05", "$25-$35", "UV-resistant outdoor parts"],
      ],
    },
    faqs: [
      { q: "How do I find how many metres of filament a print used?", a: "Your slicer software (Cura, PrusaSlicer, Bambu Studio) reports filament length and sometimes weight directly in the print preview or after slicing — copy that length figure into this calculator." },
      { q: "Why does my slicer's weight estimate differ slightly from this calculator?", a: "Slicers account for infill patterns and exact geometry per layer, while this tool assumes a uniform filament cylinder; the two should be within a few percent of each other for most standard prints." },
      { q: "Does filament diameter tolerance affect cost accuracy?", a: "Slightly — real filament varies ±0.02-0.05mm from nominal diameter, but this is a small enough variance that it won't meaningfully change cost estimates for planning purposes." },
      { q: "Should I include failed prints in cost calculations?", a: "For personal costing, no — but if pricing commercially, build in a failure rate buffer (commonly 5-15%) on top of the material cost to cover reprints." },
      { q: "How much does printer depreciation add to cost per print?", a: "A $300 printer used for 1,000 hours before major maintenance adds roughly $0.30/hour in depreciation — add this manually to the cost-per-hour figure if you want full accounting." },
      { q: "Is PETG really worth the extra cost over PLA?", a: "For outdoor, high-temperature, or mechanically stressed parts, yes — PETG's added durability and heat resistance often justifies its 10-20% price premium over PLA." },
      { q: "What electricity rate should I use if I don't know mine?", a: "Check a recent utility bill for your $/kWh rate, or use the US national average of roughly $0.16/kWh as a reasonable default." },
      { q: "Does print speed affect the cost calculation?", a: "Only indirectly through total print hours — faster prints reduce electricity cost proportionally since power draw stays roughly constant per hour of operation." },
      { q: "How can I reduce filament cost per print?", a: "Reducing infill percentage, using variable wall counts, and hollowing non-structural sections are the most effective ways to cut material weight and therefore material cost." },
      { q: "Can I use this calculator for resin printers?", a: "No — resin printers are priced by volume of liquid resin (ml) rather than filament length, so this length-based calculation doesn't apply to SLA/DLP printing." },
    ],
    formula: {
      expressions: [
        "VolumeCm3 = π × (Diameter(mm)/20)² × Length(cm)",
        "WeightG = VolumeCm3 × Density",
        "MaterialCost = (WeightG/1000) × SpoolCostPerKg",
        "PowerCost = (Watts × Hours / 1000) × Rate",
        "TotalCost = MaterialCost + PowerCost",
      ],
      variables: [
        { name: "Diameter", meaning: "Filament diameter in mm" },
        { name: "Density", meaning: "Material density g/cm³" },
        { name: "Watts", meaning: "Printer power draw" },
      ],
    },
    related: ["etsy-shipping-margin-calculator", "epoxy-resin-calculator", "llm-gpu-vram-calculator", "nas-power-cost-calculator"],
  },

  {
    slug: "kva-to-watts-calculator",
    cluster: "lifestyle",
    name: "kVA to Watts",
    h1: "Generator kVA to Watts & Running Appliance Load Calculator",
    title: "Generator kVA to Watts Calculator | Free (2026)",
    description:
      "Calculate real generator watts and safe appliance load instantly. Free online kVA to watts calculator with live formulas, unit conversion and a step-by-step breakdown.",
    keywords: [
      "generator kva to watts calculator",
      "how many watts is a 5kva generator",
      "safe continuous load generator calculator",
      "generator surge capacity calculator",
      "kva to kw conversion power factor",
      "what appliances can a 3kva generator run",
    ],
    inputs: [
      { id: "kva", label: "Generator rating", type: "number", def: "5", unit: "kVA", min: 0.1, step: 0.1 },
      { id: "pf", label: "Power factor", type: "select", def: "0.8", options: [
        { value: "0.8", label: "0.8 (typical generator)" },
        { value: "0.9", label: "0.9" },
        { value: "1.0", label: "1.0 (resistive load)" },
      ] },
      { id: "surge", label: "Surge multiplier", type: "number", def: "1.5", min: 1, max: 3, step: 0.1 },
    ],
    compute: (v) => {
      const kva = n(v, "kva"), pf = n(v, "pf"), surge = n(v, "surge");
      const realWatts = kva * pf * 1000;
      const kw = realWatts / 1000;
      const safeContinuous = realWatts * 0.8;
      const surgeCapacity = realWatts * surge;
      const surgeHeadroom = surgeCapacity - realWatts;
      return {
        cards: [
          { label: "Real watts", value: `${fmt(realWatts, 0)} W`, sub: `${fmt(kw, 2)} kW`, accent: true },
          { label: "Safe continuous load (80%)", value: `${fmt(safeContinuous, 0)} W`, sub: "Recommended max running load" },
          { label: "Surge capacity", value: `${fmt(surgeCapacity, 0)} W`, sub: `At ${surge}x multiplier` },
          { label: "Surge headroom", value: `${fmt(surgeHeadroom, 0)} W`, sub: "Available for motor start-up" },
        ],
        table: {
          title: "Generator power breakdown",
          head: ["Metric", "Value"],
          rows: [
            ["Rated kVA", `${fmt(kva, 2)} kVA`],
            ["Power factor", `${pf}`],
            ["Real watts", `${fmt(realWatts, 0)} W`],
            ["Real kW", `${fmt(kw, 2)} kW`],
            ["Safe continuous (80%)", `${fmt(safeContinuous, 0)} W`],
            ["Surge capacity", `${fmt(surgeCapacity, 0)} W`],
          ],
        },
        summary: `A ${fmt(kva, 2)} kVA generator at ${pf} power factor delivers ${fmt(realWatts, 0)}W continuously; keep sustained load under ${fmt(safeContinuous, 0)}W for reliability.`,
      };
    },
    intro:
      "Generators are rated in kVA (apparent power), but appliances are rated in watts (real power), and confusing the two leads to overloaded, stalling generators. This calculator converts kVA to real watts using power factor, then shows safe continuous and surge capacity.",
    sections: [
      { h: "kVA versus watts explained simply", body: "kVA measures the total electrical capacity a generator can supply, including reactive power that doesn't do useful work. Watts measure real, usable power. Multiplying kVA by the power factor (commonly 0.8 for generators) converts the rated capacity into the watts you can actually draw." },
      { h: "Why the 80% rule protects your generator", body: "Running a generator at its absolute maximum rated watts continuously shortens its lifespan and risks overheating or stalling under any voltage fluctuation. Manufacturers and electricians commonly recommend keeping sustained (continuous) load at or below 80% of rated watts." },
      { h: "Surge versus running watts for appliances", body: "Motors in refrigerators, air conditioners and pumps draw a short surge (often 1.5-3x their running wattage) at startup. A generator needs surge headroom above its continuous rating to start these appliances without tripping, even if it can run them fine once started." },
      { h: "Sizing a generator for your home or job site", body: "Add up the running watts of everything you'll use simultaneously, then check that the single largest motor's surge watts still fit within the generator's surge capacity above that running total — this is the most common sizing mistake." },
    ],
    staticTable: {
      title: "Common appliance power reference",
      head: ["Appliance", "Running watts", "Surge watts"],
      rows: [
        ["Refrigerator", "150-400", "800-1200"],
        ["Window AC unit (10,000 BTU)", "1200", "3600"],
        ["Microwave", "1000-1500", "1500-1800"],
        ["Well pump (1/2 hp)", "1000", "2500"],
        ["Space heater", "1500", "1500"],
        ["Laptop charger", "60-100", "100"],
        ["Sump pump", "800-1050", "1300-2200"],
      ],
    },
    faqs: [
      { q: "How many watts is a 5kVA generator?", a: "At the common 0.8 power factor, a 5kVA generator delivers about 4,000 real watts, though its nameplate may separately list a running watt figure that already accounts for this." },
      { q: "Why do generators use kVA instead of just watts?", a: "kVA reflects total electrical capacity independent of the connected load's power factor, which is more relevant to the generator's alternator design than the watts any specific appliance will draw." },
      { q: "What power factor should I use if my generator doesn't specify one?", a: "0.8 is the standard assumption for most portable and standby generators unless the nameplate or manual states otherwise." },
      { q: "Can I run my generator at 100% of its rated watts?", a: "Briefly, yes, but manufacturers recommend capping sustained load at 80% to avoid overheating, excess wear, and voltage instability during long runs." },
      { q: "What is surge (starting) wattage?", a: "It's the brief extra power (often 1.5-3x running watts) that motor-driven appliances need for a fraction of a second at startup, before settling to their running wattage." },
      { q: "How do I know if my generator can start my air conditioner?", a: "Confirm the generator's surge capacity exceeds the AC unit's surge watts plus the running watts of anything else already operating at that moment." },
      { q: "Is a higher power factor generator better?", a: "A power factor of 1.0 means all apparent power converts to real usable power, so for the same kVA rating, a higher power factor gives you more usable watts." },
      { q: "What size generator do I need for a whole house?", a: "Most homes need 5,000-10,000 running watts depending on HVAC and well pump equipment; a licensed electrician can do a precise load calculation for whole-home backup." },
      { q: "Does altitude or temperature affect generator output?", a: "Yes, generators typically lose 3-4% of rated output per 1,000 feet of elevation above sea level and can derate further in extreme heat." },
      { q: "Why did my generator trip even though I was under the rated watts?", a: "A single surge event (like an AC compressor kicking on) can momentarily exceed surge capacity even if average running load is well under the continuous rating, tripping the breaker." },
    ],
    formula: {
      expressions: [
        "RealWatts = kVA × PowerFactor × 1000",
        "SafeContinuous = RealWatts × 0.8",
        "SurgeCapacity = RealWatts × SurgeMultiplier",
      ],
      variables: [
        { name: "kVA", meaning: "Generator apparent power rating" },
        { name: "PowerFactor", meaning: "Ratio of real to apparent power (typically 0.8)" },
        { name: "SurgeMultiplier", meaning: "Peak surge relative to running watts" },
      ],
    },
    related: ["nas-power-cost-calculator", "solar-string-voltage-calculator", "3d-print-cost-calculator", "cctv-storage-calculator"],
  },

  {
    slug: "nas-power-cost-calculator",
    cluster: "lifestyle",
    name: "NAS Power Cost",
    h1: "Home Server & NAS Power Consumption Yearly Cost Calculator",
    title: "NAS & Home Server Power Cost Calculator | Free (2026)",
    description:
      "Calculate yearly electricity cost of a NAS or home server instantly. Free online power cost calculator with live formulas, unit conversion and a step-by-step breakdown.",
    keywords: [
      "nas power consumption cost calculator",
      "home server electricity cost per year",
      "how much does it cost to run a nas 24/7",
      "synology qnap power usage calculator",
      "idle vs peak watts server cost calculator",
      "5 year server electricity cost calculator",
    ],
    inputs: [
      { id: "idle", label: "Idle power draw", type: "number", def: "25", unit: "W", min: 0, step: 1 },
      { id: "peak", label: "Peak power draw", type: "number", def: "60", unit: "W", min: 0, step: 1 },
      { id: "peakHours", label: "Peak hours per day", type: "number", def: "4", unit: "hours", min: 0, max: 24, step: 0.5 },
      { id: "rate", label: "Electricity rate", type: "number", def: "0.16", unit: "per kWh", min: 0, step: 0.01 },
      { id: "currency", label: "Currency symbol", type: "select", def: "$", options: [
        { value: "$", label: "$ USD" },
        { value: "€", label: "€ EUR" },
        { value: "£", label: "£ GBP" },
        { value: "₹", label: "₹ INR" },
      ] },
    ],
    compute: (v) => {
      const idle = n(v, "idle"), peak = n(v, "peak"), peakHours = n(v, "peakHours"), rate = n(v, "rate"), currency = s(v, "currency");
      const idleHours = Math.max(0, 24 - peakHours);
      const dailyWh = idle * idleHours + peak * peakHours;
      const dailyKwh = dailyWh / 1000;
      const dailyCost = dailyKwh * rate;
      const monthlyCost = dailyCost * 30;
      const yearlyCost = dailyCost * 365;
      const fiveYearCost = yearlyCost * 5;
      return {
        cards: [
          { label: "Daily energy use", value: `${fmt(dailyKwh, 2)} kWh`, sub: `${fmt(dailyWh, 0)} Wh/day` },
          { label: "Monthly cost", value: money(monthlyCost, currency, 2), sub: "30-day estimate" },
          { label: "Yearly cost", value: money(yearlyCost, currency, 2), sub: "365-day estimate", accent: true },
          { label: "5-year cost", value: money(fiveYearCost, currency, 2), sub: "At current rate" },
        ],
        table: {
          title: "Power and cost breakdown",
          head: ["Metric", "Value"],
          rows: [
            ["Idle hours/day", `${fmt(idleHours, 1)} h`],
            ["Peak hours/day", `${fmt(peakHours, 1)} h`],
            ["Daily Wh", fmt(dailyWh, 0)],
            ["Daily kWh", fmt(dailyKwh, 2)],
            ["Daily cost", money(dailyCost, currency, 3)],
            ["Monthly cost", money(monthlyCost, currency, 2)],
            ["Yearly cost", money(yearlyCost, currency, 2)],
            ["5-year cost", money(fiveYearCost, currency, 2)],
          ],
        },
        summary: `Running this NAS 24/7 costs about ${money(yearlyCost, currency, 2)} per year, or ${money(fiveYearCost, currency, 2)} over five years, at your current electricity rate.`,
      };
    },
    intro:
      "NAS units and home servers run around the clock, so even a small difference in idle wattage compounds into a real yearly cost. This calculator blends idle and peak power draw across a typical day and projects the electricity bill over a month, year and five years.",
    sections: [
      { h: "Why idle wattage matters most", body: "Most NAS devices spend the majority of each day idle or in low-power disk-spin-down mode, with peak draw only during active transfers, transcoding or backups. Because idle hours vastly outnumber peak hours in a typical 24-hour cycle, small reductions in idle wattage — like enabling HDD hibernation — usually save more money than reducing peak load." },
      { h: "How to find your device's real wattage", body: "The most accurate approach is a plug-in power meter (like a Kill A Watt) measured over a full day, since manufacturer spec sheets often list only peak or maximum rated wattage rather than typical idle draw." },
      { h: "SSD versus HDD power differences", body: "A NAS with spinning hard drives typically idles at 15-30W depending on drive count, while an all-SSD NAS can idle under 10W. If you're comparing storage upgrade options, factor the wattage difference into the running cost over the drive's expected lifespan." },
      { h: "Comparing to cloud storage cost", body: "A 4-bay NAS running at moderate cost might total $30-60/year in electricity alone, before accounting for hardware depreciation — compare this to equivalent cloud storage subscription costs when deciding whether self-hosting is actually cheaper long-term." },
    ],
    staticTable: {
      title: "Typical NAS/server power draw by type",
      head: ["Device type", "Idle watts", "Peak watts", "Approx. yearly cost @ $0.16/kWh"],
      rows: [
        ["2-bay NAS (HDD)", "15-20", "30-40", "$25-30"],
        ["4-bay NAS (HDD)", "25-35", "50-70", "$40-55"],
        ["8-bay NAS (HDD)", "40-55", "80-110", "$65-85"],
        ["All-SSD NAS", "8-12", "20-30", "$15-20"],
        ["Mini PC home server", "10-15", "35-50", "$20-30"],
        ["Full tower home server", "60-90", "150-250", "$100-160"],
      ],
    },
    faqs: [
      { q: "How much electricity does a NAS use per year?", a: "A typical 4-bay NAS with spinning drives costs roughly $40-55 per year at US average electricity rates, running mostly idle with occasional peak activity during backups or media streaming." },
      { q: "Does spinning down hard drives actually save money?", a: "Yes, HDD hibernation during long idle periods can cut idle wattage by 30-50% on multi-bay NAS units, meaningfully reducing yearly cost since idle hours dominate the daily cycle." },
      { q: "Is it cheaper to run a NAS or use cloud storage?", a: "For pure storage under a few terabytes, cloud subscriptions are often comparable or cheaper once you include NAS hardware cost and depreciation, but NAS wins for large libraries or frequent local access." },
      { q: "How do I measure my NAS's actual power draw?", a: "A plug-in power meter placed between the wall outlet and the NAS for 24 hours will show real idle and peak wattage more accurately than manufacturer spec sheets." },
      { q: "Does running Plex or Docker containers increase power cost?", a: "Yes — active transcoding, virtual machines, or containers running continuously can push a NAS from idle into a higher sustained wattage bracket for as long as they're active." },
      { q: "What's the difference between watts and kWh?", a: "Watts measure instantaneous power draw; kilowatt-hours measure energy consumed over time, which is what your electricity bill actually charges you for — watts × hours ÷ 1000 = kWh." },
      { q: "How much does it cost to leave a NAS on 24/7 for a month?", a: "For a typical 4-bay NAS averaging around 30W blended draw, expect roughly $3.50-$4.50 per month at $0.16/kWh, though this varies with your specific rate and usage pattern." },
      { q: "Do SSDs really use less power than HDDs in a NAS?", a: "Yes, SSDs have no spinning platters or motors, typically drawing 2-5W each versus 4-8W per active HDD, which adds up meaningfully in multi-bay enclosures." },
      { q: "Should I factor in UPS or networking equipment power draw too?", a: "For a full home-lab electricity picture, yes — add your router, switch, and UPS standby draw separately, since this calculator focuses specifically on the NAS or server unit itself." },
      { q: "Will electricity rate increases significantly change my 5-year estimate?", a: "Yes, this calculator uses your current rate as a flat projection; if rates rise (common at 2-5% per year in many regions), actual 5-year cost will run somewhat higher than shown." },
    ],
    formula: {
      expressions: [
        "IdleHours = 24 − PeakHours",
        "DailyWh = Idle × IdleHours + Peak × PeakHours",
        "DailyKwh = DailyWh / 1000",
        "YearlyCost = DailyKwh × Rate × 365",
      ],
      variables: [
        { name: "Idle", meaning: "Idle power draw in watts" },
        { name: "Peak", meaning: "Peak/active power draw in watts" },
        { name: "PeakHours", meaning: "Hours per day at peak draw" },
      ],
    },
    related: ["kva-to-watts-calculator", "3d-print-cost-calculator", "cctv-storage-calculator", "llm-gpu-vram-calculator"],
  },
];
